if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.initElements();

        this.mql = window.matchMedia('(min-width: 750px)');

        this.initListeners();
        
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        
        // Auto-play videos when the gallery loads
        this.setupAutoPlay();
      }

      refreshElements() {
        this.initElements();
        this.initListeners();
      }

      initElements() {
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
      }

      initListeners() {
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');

        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      setupAutoPlay() {
        // Wait for DOM to be fully loaded and rendered
        setTimeout(() => {
          // Try a different approach - directly load content for deferred media
          const deferredMedias = this.querySelectorAll('.deferred-media');
          
          if (deferredMedias.length > 0) {
            deferredMedias.forEach((deferredMedia, index) => {
              // Try to directly load content using the loadContent method
              if (typeof deferredMedia.loadContent === 'function') {
                deferredMedia.loadContent(false);
              } else {
                // Try to find the template and create the video element manually
                const template = deferredMedia.querySelector('template');
                if (template) {
                  try {
                    // Create a clone of the template content
                    const clone = template.content.cloneNode(true);
                    // Clear the deferred media and append the clone
                    deferredMedia.innerHTML = '';
                    deferredMedia.appendChild(clone);
                    
                    // Try to find and play video elements
                    const video = deferredMedia.querySelector('video');
                    if (video) {
                      video.play().catch(e => {});
                    }
                    
                    // Try to find and play iframe elements (YouTube, Vimeo)
                    const iframe = deferredMedia.querySelector('iframe');
                    if (iframe) {
                      // For YouTube
                      if (iframe.src.includes('youtube')) {
                        iframe.src = iframe.src.replace('autoplay=0', 'autoplay=1');
                        if (!iframe.src.includes('autoplay=')) {
                          iframe.src += (iframe.src.includes('?') ? '&' : '?') + 'autoplay=1';
                        }
                      }
                      // For Vimeo
                      else if (iframe.src.includes('vimeo')) {
                        iframe.src = iframe.src.replace('autoplay=0', 'autoplay=1');
                        if (!iframe.src.includes('autoplay=')) {
                          iframe.src += (iframe.src.includes('?') ? '&' : '?') + 'autoplay=1';
                        }
                      }
                    }
                  } catch (error) {
                    // Silent error handling
                  }
                }
              }
            });
          }
          
          // Also try to find the active media and play it using a different approach
          const activeMedia = this.elements.viewer.querySelector('.is-active[data-media-id]') || 
                             this.elements.viewer.querySelector('[data-media-id]');
          
          if (activeMedia) {
            // Try to find and play video elements directly
            const video = activeMedia.querySelector('video');
            if (video) {
              video.muted = true; // Mute to allow autoplay
              video.play().catch(e => {});
            }
            
            // Try to find and play iframe elements (YouTube, Vimeo)
            const iframe = activeMedia.querySelector('iframe');
            if (iframe) {
              // For YouTube
              if (iframe.src.includes('youtube')) {
                iframe.src = iframe.src.replace('autoplay=0', 'autoplay=1');
                if (!iframe.src.includes('autoplay=')) {
                  iframe.src += (iframe.src.includes('?') ? '&' : '?') + 'autoplay=1';
                }
              }
              // For Vimeo
              else if (iframe.src.includes('vimeo')) {
                iframe.src = iframe.src.replace('autoplay=0', 'autoplay=1');
                if (!iframe.src.includes('autoplay=')) {
                  iframe.src += (iframe.src.includes('?') ? '&' : '?') + 'autoplay=1';
                }
              }
            }
          }
        }, 1000); // Wait 1 second to ensure elements are fully loaded
      }
    }
  );
}
